import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getBytes,
  listAll,
  getMetadata,
} from "firebase/storage";
import { TEAMS } from "./constants.mjs";
import { formatLapTime } from "./utils.mjs";
import { getGhostArrayBuffer, parseGhostFromArrayBuffer } from "./ghost.mjs";

const firebaseConfig = {
  apiKey: "AIzaSyDdozoyQCF0P9Iia3N9UI8-EMdT1TrLEtM",
  authDomain: "f1-sim-game.firebaseapp.com",
  projectId: "f1-sim-game",
  storageBucket: "f1-sim-game.firebasestorage.app",
  messagingSenderId: "519679428139",
  appId: "1:519679428139:web:49c16ce1ae3e94a5ac4c6e",
  measurementId: "G-LPTK1C7S0T",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const storage = getStorage(app);
export const loadedGhosts = {};

// ---------- Helpers ----------
function pad6(n) {
  return n.toString().padStart(6, "0");
}

function sanitizeTrackId(id) {
  return id.replace(/[^a-z0-9_\-]/gi, "_");
}

function sanitizeName3(name) {
  const n = (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (n.length !== 3) throw new Error("Name must be exactly 3 alphanumerics");
  return n;
}

function sanitizeTeamIndex(idx) {
  const n = parseInt(idx, 10);
  if (!Number.isFinite(n)) return 0;
  if (n < 0 || n >= TEAMS.length) return 0;
  return n;
}

/**
 * Parse ghost filename into lapMs, name3, teamIndex
 * Expected pattern: "{lapMs}_{NAME3}_{teamIndex}_{uuid}.gh3"
 */
function parseEntryName(fileName) {
  const m = /^(\d+)_([A-Z0-9]{3})_([0-9])_([a-f0-9-]+)\.gh3$/i.exec(fileName);
  if (!m) return null;

  const lapMs = parseInt(m[1], 10);
  // store internally as lowercase for consistency
  const name3 = m[2].toLowerCase();
  const teamIndex = parseInt(m[3], 10);
  const uuid = m[4];

  return { lapMs, name3, teamIndex, uuid };
}

// ---------- Leaderboard ----------
export async function loadLeaderboard(trackId, useDummyWhenEmpty = false) {
  const tid = sanitizeTrackId(trackId);
  const dirRef = sRef(storage, `ghosts/${tid}`);
  let items = [];
  try {
    const res = await listAll(dirRef);
    items = res.items || [];
  } catch (error) {
    items = [];
  }

  // Parse all entries
  const parsed = [];
  for (const it of items) {
    const parsedName = parseEntryName(it.name);
    if (!parsedName) continue;
    parsed.push({
      trackId: tid,
      path: it.fullPath,
      lapMs: parsedName.lapMs,
      name3: parsedName.name3, // already lowercase
      teamIndex: parsedName.teamIndex,
      file: it.name,
    });
  }

  // ---- NEW: dedupe by name (case-insensitive), keep best lap per name ----
  const bestByName = new Map(); // key: lowercase name, value: entry
  for (const entry of parsed) {
    const key = entry.name3.toLowerCase();
    const existing = bestByName.get(key);
    if (!existing || entry.lapMs < existing.lapMs) {
      bestByName.set(key, entry);
    }
  }

  const uniqueEntries = Array.from(bestByName.values());

  // Sort by lapMs ascending and keep top 10
  uniqueEntries.sort((a, b) => a.lapMs - b.lapMs);
  const result = uniqueEntries.slice(0, 10);

  if (useDummyWhenEmpty && result.length === 0) {
    return generateDummyLeaderboard();
  }

  // Pad to 10 entries with blanks
  for (let i = result.length; i < 10; i++) {
    result.push({
      trackId,
      path: null,
      lapMs: -1,
      name3: "---",
      teamIndex: -1,
      file: null,
    });
  }

  return result;
}

// ---------- Date helpers for weekly/monthly filtering ----------
function getWeekStartDate(date = new Date()) {
  const d = new Date(date);
  const day = d.getUTCDay();
  // Convert Sunday (0) to 7 for easier Monday-based calculation
  const diff = day === 0 ? 6 : day - 1;
  d.setUTCDate(d.getUTCDate() - diff);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

function getMonthStartDate(date = new Date()) {
  const d = new Date(date);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

// Load leaderboard entries with their creation timestamps
async function loadEntriesWithTimestamps(trackId) {
  const tid = sanitizeTrackId(trackId);
  const dirRef = sRef(storage, `ghosts/${tid}`);
  let items = [];
  try {
    const res = await listAll(dirRef);
    items = res.items || [];
  } catch (error) {
    items = [];
  }

  // Parse all entries and fetch metadata for timestamps
  const entries = [];
  const metadataPromises = items.map(async (it) => {
    const parsedName = parseEntryName(it.name);
    if (!parsedName) return null;

    try {
      const metadata = await getMetadata(it);
      return {
        trackId: tid,
        path: it.fullPath,
        lapMs: parsedName.lapMs,
        name3: parsedName.name3,
        teamIndex: parsedName.teamIndex,
        file: it.name,
        createdAt: new Date(metadata.timeCreated),
      };
    } catch (error) {
      // If metadata fetch fails, still include the entry without timestamp
      return {
        trackId: tid,
        path: it.fullPath,
        lapMs: parsedName.lapMs,
        name3: parsedName.name3,
        teamIndex: parsedName.teamIndex,
        file: it.name,
        createdAt: null,
      };
    }
  });

  const results = await Promise.all(metadataPromises);
  return results.filter((entry) => entry !== null);
}

function filterAndSortLeaderboard(entries, filterFn = null) {
  // Apply date filter if provided
  let filtered = filterFn ? entries.filter(filterFn) : entries;

  // Dedupe by name (case-insensitive), keep best lap per name
  const bestByName = new Map();
  for (const entry of filtered) {
    const key = entry.name3.toLowerCase();
    const existing = bestByName.get(key);
    if (!existing || entry.lapMs < existing.lapMs) {
      bestByName.set(key, entry);
    }
  }

  const uniqueEntries = Array.from(bestByName.values());

  // Sort by lapMs ascending and keep top 10
  uniqueEntries.sort((a, b) => a.lapMs - b.lapMs);
  const result = uniqueEntries.slice(0, 10);

  // Pad to 10 entries with blanks
  for (let i = result.length; i < 10; i++) {
    result.push({
      trackId: filtered[0]?.trackId || "",
      path: null,
      lapMs: -1,
      name3: "---",
      teamIndex: -1,
      file: null,
      createdAt: null,
    });
  }

  return result;
}

// Cache for entries with timestamps to avoid multiple fetches
let cachedEntriesTrackId = null;
let cachedEntries = null;

export async function loadAllLeaderboards(trackId) {
  // Fetch entries with timestamps (cached per track)
  if (cachedEntriesTrackId !== trackId) {
    cachedEntries = await loadEntriesWithTimestamps(trackId);
    cachedEntriesTrackId = trackId;
  }

  const now = new Date();
  const weekStart = getWeekStartDate(now);
  const monthStart = getMonthStartDate(now);

  const weekly = filterAndSortLeaderboard(
    cachedEntries,
    (entry) => entry.createdAt && entry.createdAt >= weekStart
  );

  const monthly = filterAndSortLeaderboard(
    cachedEntries,
    (entry) => entry.createdAt && entry.createdAt >= monthStart
  );

  const allTime = filterAndSortLeaderboard(cachedEntries, null);

  return { weekly, monthly, allTime };
}

export function clearLeaderboardCache() {
  cachedEntriesTrackId = null;
  cachedEntries = null;
}

function generateDummyLeaderboard() {
  const result = [];
  for (let i = 0; i < 10; i++) {
    result.push({
      trackId: "test",
      path: "/test",
      lapMs: 60000 + i * 1000,
      name3: "USR",
      teamIndex: i,
      file: "test",
    });
  }
  return result;
}

export function displayLoadingLeaderboard($leaderboard) {
  $leaderboard.innerHTML = "";
  const $loadingSpinner = document.createElement("tr");
  $loadingSpinner.innerHTML = `<td colspan="4"><div class="spinner-wrapper"><div class="spinner"></div><div>Loading leaderboard...</div></div></td>`;
  $loadingSpinner.className = "spinner-container";
  $leaderboard.appendChild($loadingSpinner);
}

export function displayLeaderboard($leaderboard, leaderboard) {
  $leaderboard.innerHTML = "";
  for (let i = 0; i < leaderboard.length; i++) {
    const entry = leaderboard[i];
    const team = entry.teamIndex >= 0 ? TEAMS[entry.teamIndex] : null;
    const $entryRow = document.createElement("tr");
    $entryRow.innerHTML = `
        <td>${i + 1}</td>
        <td class="team-cell">
          ${
            team
              ? `<div class="color-box">
            <div class="color-swatch" style="background:${team.primary}"></div>
            <div class="color-swatch" style="background:${team.secondary}"></div>
            <div class="color-swatch" style="background:${team.accent}"></div>
          </div>
          <span class="team-name">${team.name}</span>`
              : "---"
          }
        </td>
        <td class="entry-name">${
          entry.name3 === "---" ? "---" : entry.name3.toUpperCase()
        }</td>
        <td>${entry.lapMs >= 0 ? formatLapTime(entry.lapMs) : "---"}</td>
    `;
    $leaderboard.appendChild($entryRow);
  }
}

export function resetBestLapDialog($leaderboard) {
  const $entryRows = $leaderboard.querySelectorAll("tr");
  $entryRows.forEach(($entryRow) => {
    $entryRow.className = "";
  });
}

export function setLeaderboardEntry($leaderboard, rank, entry) {
  const team = entry.teamIndex >= 0 ? TEAMS[entry.teamIndex] : null;
  const $entryRow = $leaderboard.querySelectorAll("tr")[rank];
  $entryRow.className = "selected";
  $entryRow.innerHTML = `
      <td>${rank + 1}</td>
      <td class="team-cell">
        ${
          team
            ? `<div class="color-box">
          <div class="color-swatch" style="background:${team.primary}"></div>
          <div class="color-swatch" style="background:${team.secondary}"></div>
          <div class="color-swatch" style="background:${team.accent}"></div>
        </div>
        <span class="team-name">${team.name}</span>`
            : "---"
        }
      </td>
      <td class="entry-name">${entry.name3 || "---"}</td>
      <td>${entry.lapMs >= 0 ? formatLapTime(entry.lapMs) : "---"}</td>
  `;
  return $entryRow;
}

export function insertLeaderboardEntryAt($leaderboard, rank, entry) {
  const team = entry.teamIndex >= 0 ? TEAMS[entry.teamIndex] : null;
  const $entryRows = $leaderboard.querySelectorAll("tr");
  const $insertedEntryRow = document.createElement("tr");
  $insertedEntryRow.className = "selected";
  $insertedEntryRow.innerHTML = `
      <td>${rank + 1}</td>
      <td class="team-cell">
        ${
          team
            ? `<div class="color-box">
          <div class="color-swatch" style="background:${team.primary}"></div>
          <div class="color-swatch" style="background:${team.secondary}"></div>
          <div class="color-swatch" style="background:${team.accent}"></div>
        </div>
        <span class="team-name">${team.name}</span>`
            : "---"
        }
      </td>
      <td class="entry-name">${entry.name3 || "---"}</td>
      <td>${entry.lapMs >= 0 ? formatLapTime(entry.lapMs) : "---"}</td>
  `;
  $leaderboard.insertBefore($insertedEntryRow, $entryRows[rank]);

  for (let i = rank; i < $entryRows.length; i++) {
    $entryRows[i].querySelectorAll("td")[0].innerText = i + 2;
  }

  if ($entryRows.length === 10) {
    const $lastEntryRow = $entryRows[$entryRows.length - 1];
    $lastEntryRow.parentNode.removeChild($lastEntryRow);
  }

  return $insertedEntryRow;
}

export function setSelectedEntryName($leaderboard, name) {
  const $entryName = $leaderboard.querySelector("tr.selected td.entry-name");
  $entryName.innerText = name;
}

export function getWorstLapTime(leaderboard) {
  const worstLapTime = -1;
  let lapTime;
  for (let i = 0; i < leaderboard.length; i++) {
    lapTime = leaderboard[i].lapMs;
    if (worstLapTime < 0 || (lapTime >= 0 && worstLapTime > lapTime)) {
      worstLapTime = lapTime;
    }
  }
  return worstLapTime;
}

// ---------- Load a single ghost ----------
async function loadGhostBinaryOnce(path, timeoutMs = 5000) {
  // Wrap the fetch in a timeout to fail fast on CORS/network issues
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Timeout after ${timeoutMs}ms`)), timeoutMs)
  );

  const fetchPromise = getBytes(sRef(storage, path));

  const u8 = await Promise.race([fetchPromise, timeoutPromise]);
  return parseGhostFromArrayBuffer(u8);
}

export async function loadGhostBinary(path, maxRetries = 3) {
  // Return cached ghost if already loaded
  if (loadedGhosts[path]) {
    return loadedGhosts[path];
  }

  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const ghostData = await loadGhostBinaryOnce(path);
      loadedGhosts[path] = ghostData;
      return ghostData;
    } catch (error) {
      lastError = error;
      console.warn(
        `Failed to load ghost ${path} (attempt ${attempt}/${maxRetries}):`,
        error.message || error
      );

      // Don't retry on the last attempt
      if (attempt < maxRetries) {
        // Wait a bit before retrying (exponential backoff: 100ms, 200ms)
        await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, attempt - 1)));
      }
    }
  }

  // All retries failed
  console.error(
    `Failed to load ghost ${path} after ${maxRetries} attempts. Continuing without this ghost.`,
    lastError
  );
  return null;
}

// ---------- Submit lap ----------
export function getLapTimeRank(leaderboard, lapMs) {
  let rank = 0;
  for (let i = 0; i < leaderboard.length; i++) {
    if (leaderboard[i].lapMs >= 0 && lapMs >= leaderboard[i].lapMs) {
      rank = i + 1;
    }
  }

  return rank >= 0 && rank < leaderboard.length ? rank : -1;
}

export async function submitBestLapTime({
  trackId,
  name3,
  teamIndex,
  samples,
}) {
  const tid = sanitizeTrackId(trackId);
  const NAME3 = sanitizeName3(name3);
  const TEAM_INDEX = sanitizeTeamIndex(teamIndex);
  const lastSample = samples[samples.length - 1];
  const lapMs = Math.round(lastSample.t * 1000); // seconds -> ms

  if (!Number.isFinite(lapMs) || lapMs <= 0)
    throw new Error("Invalid lapMs", lapMs);

  const buffer = getGhostArrayBuffer(samples);
  if (!(buffer instanceof ArrayBuffer))
    throw new Error("buffer must be ArrayBuffer");

  const leaderboard = await loadLeaderboard(tid);

  const rank = getLapTimeRank(leaderboard, lapMs);

  if (rank < 0) {
    return { accepted: false, reason: "Not in top 10", leaderboard };
  }

  const uuid =
    (crypto.randomUUID && crypto.randomUUID()) ||
    Math.random().toString(16).slice(2);

  // include teamIndex
  const fname = `${lapMs}_${NAME3}_${TEAM_INDEX}_${uuid}.gh3`;
  const path = `ghosts/${tid}/${fname}`;
  const bytes = new Uint8Array(buffer);

  if (bytes.byteLength > 256 * 1024) {
    return { accepted: false, reason: "File too large" };
  }

  await uploadBytes(sRef(storage, path), bytes, {
    contentType: "application/octet-stream",
  });

  const updated = await loadLeaderboard(tid);
  return { accepted: true, path, leaderboard: updated };
}
